/**
 * The SEMH rating scale (confidence, resilience, independence, motivation, self-regulation).
 *
 * Ratings are **1..5** (very low..very high) — the API contract, see Ilp in
 * TutorialPortal.Entities/Ilp/IlpModels.cs. The SPA previously treated them as a 0-based index,
 * which rendered every label one notch low.
 *
 * This file is the single definition of the scale, its labels and its colours: the ILP screen, the
 * progress report, both timeline cards and the QA review table all read it. Before it existed the
 * label array was copy-pasted into four components and the colour ramp into a fifth.
 */
export const SEMH_MIN = 1;
export const SEMH_MAX = 5;

export const SEMH_LABELS: ReadonlyArray<string> = ['Very low', 'Low', 'Moderate', 'High', 'Very high'];

/** Red → green, matching the slider's own ramp. Indexed by (rating - SEMH_MIN). */
export const SEMH_COLOURS: ReadonlyArray<string> = ['#cc0000', '#e65100', '#f57f17', '#6db33f', '#2e7d32'];

/**
 * Normalises a stored rating into 1..5.
 *
 * Rows written before the scale was corrected hold 0-based values. A stored 0 meant 'Very low' —
 * the old UI rendered labels[0] for it — and an untouched slider also sat at 0, so clamping 0 (and
 * null) up to 1 preserves the tutor's intent exactly. Values 1..4 are valid under *both* scales and
 * cannot be told apart from the data, so they are left alone and legacy rows in that range read one
 * notch high until the next save rewrites them. There is deliberately no SQL migration.
 */
export function clampSemh(value: number | null | undefined): number {
  if (value == null || !Number.isFinite(value) || value < SEMH_MIN) {
    return SEMH_MIN;
  }
  return value > SEMH_MAX ? SEMH_MAX : Math.round(value);
}

export function semhLabel(value: number | null | undefined): string {
  return SEMH_LABELS[clampSemh(value) - SEMH_MIN];
}

export function semhColour(value: number | null | undefined): string {
  return SEMH_COLOURS[clampSemh(value) - SEMH_MIN];
}
